import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3,
  Check,
  Eye,
  Inbox,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { visibleApps } from "@/data/apps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tutinsapp_admin_token";

const field =
  "w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors duration-300 placeholder:text-gray-600 focus:border-[var(--accent)]";
const fieldStyle = { background: "var(--ink-2)", border: "1px solid var(--line)" };
const panel = { background: "var(--surface)", border: "1px solid var(--line)" };

const errText = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong";

const Login = ({ onDone }) => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onDone(data.username);
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell flex min-h-screen items-center justify-center py-24">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl p-8"
        style={panel}
      >
        <p className="label">Restricted</p>
        <h1 className="font-display mt-4 text-2xl font-black tracking-tight text-white">
          Admin sign in
        </h1>
        <label className="mt-8 block">
          <span className="label mb-2 block">Username</span>
          <input
            data-testid="admin-login-username"
            required
            autoComplete="username"
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            className={field}
            style={fieldStyle}
          />
        </label>
        <label className="mt-4 block">
          <span className="label mb-2 block">Password</span>
          <input
            data-testid="admin-login-password"
            required
            type="password"
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className={field}
            style={fieldStyle}
          />
        </label>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-[#08090d] disabled:opacity-60"
          style={{ background: "var(--accent)" }}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign in
        </button>
      </form>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl p-5" style={panel} data-testid={testId}>
    <Icon className="h-4 w-4" style={{ color: "var(--accent)" }} aria-hidden />
    <p className="font-display mt-4 text-3xl font-black text-white">{value}</p>
    <p className="mt-1 font-mono-x text-[10px] uppercase tracking-[0.16em] text-gray-500">{label}</p>
  </div>
);

const Bars = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.count));
  return (
    <div className="rounded-2xl p-4 sm:p-6" style={panel} data-testid="admin-daily-chart">
      <p className="label">Visits · last 14 days</p>
      <div className="mt-8 flex h-44 min-w-0 items-end gap-1 sm:gap-2">
        {daily.map((d) => (
          <div key={d.day} className="group flex min-w-0 flex-1 flex-col items-center justify-end gap-2">
            <span className="text-[10px] text-gray-500 opacity-0 transition-opacity group-hover:opacity-100">
              {d.count}
            </span>
            <div
              className="w-full rounded-t-md transition-[height] duration-700"
              style={{
                height: `${Math.max(4, Math.round((d.count / max) * 130))}px`,
                background: d.count ? "var(--accent)" : "rgba(255,255,255,0.08)",
              }}
            />
            <span className="font-mono-x text-[9px] text-gray-600">{d.day.slice(8)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const List = ({ title, items, testId }) => (
  <div className="min-w-0 rounded-2xl p-4 sm:p-6" style={panel} data-testid={testId}>
    <p className="label">{title}</p>
    <ul className="mt-6 space-y-3">
      {items.length === 0 && <li className="text-sm text-gray-500">No data yet.</li>}
      {items.map((i) => (
        <li key={i.label} className="flex min-w-0 items-center justify-between gap-4 text-sm">
          <span className="min-w-0 truncate text-gray-300">{i.label}</span>
          <span className="font-mono-x text-xs text-white">{i.count}</span>
        </li>
      ))}
    </ul>
  </div>
);

const appName = (id) => visibleApps.find((a) => a.id === id)?.name || id;

const Message = ({ m, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const handled = m.status === "handled";
  return (
    <li className="rounded-2xl p-5" style={panel} data-testid={`admin-message-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`admin-message-toggle-${m.id}`}
          className="flex-1 text-left"
        >
          <p className="flex flex-wrap items-center gap-2 font-mono-x text-[10px] uppercase tracking-[0.14em] text-gray-500">
            <span style={{ color: handled ? "#6B7280" : "var(--accent)" }}>
              {handled ? "handled" : "new"}
            </span>
            <span>· {m.ticket_ref}</span>
            <span>· {appName(m.app_id)}</span>
            <span>· {m.topic}</span>
            <span>· {new Date(m.created_at).toLocaleString()}</span>
          </p>
          <p className="font-display mt-2 text-base font-bold text-white">{m.subject}</p>
          <p className="mt-1 text-sm text-gray-400">
            {m.name} — {m.email}
          </p>
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onStatus(m.id, handled ? "new" : "handled")}
            data-testid={`admin-message-handled-${m.id}`}
            title={handled ? "Mark as new" : "Mark as handled"}
            className="rounded-full p-2.5 text-gray-400 transition-colors hover:text-white"
            style={{ border: "1px solid var(--line)" }}
          >
            <Check className="h-4 w-4" />
          </button>
          <a
            href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)} (${m.ticket_ref})`}
            data-testid={`admin-message-reply-${m.id}`}
            title="Reply by email"
            className="rounded-full p-2.5 text-gray-400 transition-colors hover:text-white"
            style={{ border: "1px solid var(--line)" }}
          >
            <Mail className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={() => onDelete(m.id)}
            data-testid={`admin-message-delete-${m.id}`}
            title="Delete"
            className="rounded-full p-2.5 text-gray-500 transition-colors hover:text-red-400"
            style={{ border: "1px solid var(--line)" }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--line)" }}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{m.message}</p>
          {m.device && <p className="mt-3 font-mono-x text-xs text-gray-500">Device: {m.device}</p>}
        </div>
      )}
    </li>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStats(null);
    setMessages([]);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(s.data);
      setMessages(m.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired — please sign in again");
        logout();
      } else {
        toast.error("Could not load admin data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/messages/${id}?status=${status}`, {}, auth);
      setMessages((list) => list.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch {
      toast.error("Could not update the message");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, auth);
      setMessages((list) => list.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch {
      toast.error("Could not delete the message");
    }
  };

  if (!token) return <Login onDone={() => setToken(localStorage.getItem(TOKEN_KEY))} />;

  const shown = filter === "all" ? messages : messages.filter((m) => m.app_id === filter);

  return (
    <div className="shell py-28 sm:py-32" data-testid="admin-dashboard">
      <Seo title="Admin" path="/admin" description="Private admin area." />

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="label">tutinsapp · private</p>
          <h1 className="font-display mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl">
            Admin dashboard
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-gray-300 transition-colors hover:text-white"
            style={{ border: "1px solid var(--line)" }}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-gray-300 transition-colors hover:text-white"
            style={{ border: "1px solid var(--line)" }}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      {stats && (
        <>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Stat icon={Eye} label="Total visits" value={stats.total_visits} testId="admin-stat-total" />
            <Stat icon={BarChart3} label="Visits today" value={stats.today_visits} testId="admin-stat-today" />
            <Stat icon={BarChart3} label="Visits · 7 days" value={stats.week_visits} testId="admin-stat-week" />
            <Stat icon={Users} label="Unique visitors" value={stats.unique_visitors} testId="admin-stat-unique" />
            <Stat icon={Inbox} label="New messages" value={stats.new_messages} testId="admin-stat-new-messages" />
          </div>

          <div className="mt-6 grid min-w-0 gap-6 lg:grid-cols-[1.6fr_1fr]">
            <Bars daily={stats.daily} />
            <div className="grid min-w-0 gap-6">
              <List title="Top pages" items={stats.top_pages} testId="admin-top-pages" />
              <List title="Top referrers" items={stats.top_referrers} testId="admin-top-referrers" />
            </div>
          </div>
        </>
      )}

      <section className="mt-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="label">Customer messages ({messages.length})</p>
          <div className="flex flex-wrap gap-2">
            {[{ id: "all", name: "All apps" }, ...visibleApps].map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setFilter(a.id)}
                data-testid={`admin-filter-${a.id}`}
                className="rounded-full px-4 py-2 text-xs transition-colors duration-300"
                style={
                  filter === a.id
                    ? { background: "var(--accent)", color: "#08090d" }
                    : { border: "1px solid var(--line)", color: "#9CA3AF" }
                }
              >
                {a.name}
              </button>
            ))}
          </div>
        </div>

        <ul className="mt-8 space-y-4">
          {shown.length === 0 && (
            <li data-testid="admin-messages-empty" className="text-sm text-gray-500">
              No messages yet.
            </li>
          )}
          {shown.map((m) => (
            <Message key={m.id} m={m} onStatus={setStatus} onDelete={remove} />
          ))}
        </ul>
      </section>
    </div>
  );
}
